<template>
  <div class="pump-curve" :style="{'width':`${curvesWidth}px`}">
    <curve-points-filter 
      v-if="designPropertyData&&operatingData"
      :design-property-data="designPropertyData"
      :operating-data="operatingData"
      @on-design-checked="designChange"
      @on-operating-checked="operatingChange"
      @on-speed-regulated="speedRegulateChange"
      @track="$emit('track-speed-regulated')" />
    <div class="performance-curve-container">
      <div class="curve-selection-no-speed">
        <span>曲线</span>
      </div>
      <div ref="curveChart" class="performance-curve" :style="{'width':`${curvesWidth}px`,'height':`${curvesHeight}px`}" />
    </div>
  </div>
</template>
<script>
import echarts from '@/plugins/echarts'
import CurvePointsFilter from './CurvePointsFilter.vue'
import utils from '@/core/utils'
const SPEC_Y_AXIS = ['流量', '扬程', '轴功率']
export default {
  name: 'PumpCurve',
  components: {
    CurvePointsFilter
  },
  props: {
    performanceCurveData: {
      type: Object,
      required: true
    },
    curvesWidth: {
      type: Number,
      default: 752
    },
    curvesHeight: {
      type: Number,
      default: 576
    },
    colors: {
      type: Object,
      default() {
        return {
          deepCurveColors: {
            扬程: 'rgb(58, 121, 243)',
            工作压力: 'rgb(146, 20, 190)',
            效率: 'rgb(217, 35, 111)',
            汽蚀余量: 'rgb(252, 135, 27)',
            轴功率: 'rgb(152, 201, 13)'  
          },
          shallowCurveColors: {
            扬程: 'rgba(58, 121, 243, 0.5)',
            工作压力: 'rgba(146, 20, 190, 0.5)',
            效率: 'rgba(217, 35, 111, 0.5)',
            汽蚀余量: 'rgba(252, 135, 27, 0.5)',
            轴功率: 'rgba(152, 201, 13, 0.5)'
          },
          parabolaColor: '#8892A0',
        }
      }
    },
    refreshSpeedTimes: {
      type: Number,
      default: 0
    }
  },
  watch: {
    'refreshSpeedTimes'(newValue) {
      // 监测数值变化为了执行展示调速后新曲线
      if (newValue) {
        this.showSpeedRegulationCurves()
      }
    }
  },
  data() {
    return {
      // option初始设置
      option: {
        animation: false,
        axisPointer: {
          link: { xAxisIndex: 'all' }
        },
        tooltip: {
          trigger: 'none',
          axisPointer: {
            type: 'cross'
          },
        },
      },
      myChart: null,
      showDesignPoints: true,
      showOperatingPoints: true,
      speedRegulated: false
    }
  },
  computed: {
    parabolas() {
      console.log('this.performanceCurveData.parabolas:', this.performanceCurveData.parabolas)
      return this.performanceCurveData.parabolas
    },
    designPropertyData() {
      return this.performanceCurveData.designPropertyData
    },
    operatingData() {
      return this.performanceCurveData.operatingData
    },
    speedRegulationCurves() {
      return this.performanceCurveData.speedRegulationCurves
    }
  },
  methods: {
    init() {
    // myChart初始化一次即可
      if (!this.myChart) {
        this.generateCurves()
      }
      this.myChart.on('legendselectchanged', (params) => {
        this.legendChanged(params)
      })
    },
    legendChanged(params) {
      if (this.speedRegulated) {
        const oldSelected = this.option.legend.selected
        const { name, selected } = params
        const value = selected[name]
        const newSelected = {}
        this.option.series.filter((s) => s.name.indexOf(name) > -1).forEach((item) => {
          newSelected[item.name] = value // 调速曲线图例控制
        })
        this.option.legend.selected = {
          ...oldSelected,
          ...newSelected,
          ...selected
        }
        this.showCurve(true)
      }
    },
    showCurve(refresh = false) {
      const curveChart = this.$refs.curveChart
      if (curveChart) {
        if (!this.myChart) {
          this.myChart = echarts.init(curveChart)
        }
        this.myChart.setOption(this.option, refresh)
        console.log('this.option', this.option)
      }
    },
    getYAxis(name) {
      const yAxises = this.performanceCurveData.yAxises
      const axis = yAxises.find((a) => a.name === name)
      return axis || {}
    },
    getDefaultSeriesOption(curve) {
      return {
        name: curve.yName,
        type: 'line',
        smooth: true,
        showSymbol: false,
        endLabel: {
          show: false,
          align: 'left',
          formatter: '',
          color: 'inherit',
          offset: [0, 10]
        },
        data: curve.data
      }
    },
    getParabolaSeries() {
      const series = []
      const parabolas = this.parabolas
      if (this.showDesignPoints && parabolas.length) {
        parabolas.forEach((p, index) => {
          series.push({
            name: `parabola${index}`,
            smooth: true,
            xAxisIndex: 0,
            yAxisIndex: 0,
            type: 'line',
            showSymbol: false,
            data: p.data,
            color: this.colors.parabolaColor,
            markPoint: {
              symbol: 'circle',
              data: [
                {
                  coord: p.designPoint,
                  symbolSize: 10,
                  itemStyle: {
                    color: this.colors.parabolaColor,
                  }
                }
              ]
            }
          })
        })
      }
      return series
    },
    getOperatingMarkPoint(operatingPoint, name) {
      let markPoint = null
      if (this.showOperatingPoints && this.operatingData && operatingPoint) {
        markPoint = {
          symbol: 'circle',
          data: [
            {
              coord: operatingPoint,
              symbolSize: 10,
            }
          ],
          itemStyle: {
            color: this.colors.deepCurveColors[name]
          }
        }
      }
      return markPoint
    },
    generateCurves() {
      const { xAxises, yAxises, curves } = this.performanceCurveData
      const xAxis = xAxises[0]
      const yLength = yAxises.length
      const option = this.option
      if (yLength) {
        if (yLength <= 3) {
          option.grid = [{ right: yLength <= 2 ? 75 : 150 }]
          option.xAxis = [
            {
              type: 'value',
              gridIndex: 0,
              min: this.speedRegulated ? xAxis.rMin : xAxis.min,
              max: xAxis.max,
              interval: xAxis.interval,
              name: `${xAxis.name}(${xAxis.unit})`,
              nameLocation: 'middle',
              nameTextStyle: {
                padding: 16
              },
              axisLine: {
                lineStyle: {
                  color: '#14141E'
                }
              },
              axisLabel: {
                showMinLabel: true
              },
            }
          ]
        } else {
          option.grid = [
            { bottom: '52%', right: 150 },
            { top: '52%', right: 150 }
          ]
          option.xAxis = [
            {
              type: 'value',
              gridIndex: 0,
              min: xAxis.min,
              max: xAxis.max,
              interval: xAxis.interval,
              axisLabel: {
                show: false,
              },
              axisLine: {
                lineStyle: {
                  color: '#14141E'
                }
              },
            },
            {
              type: 'value',
              gridIndex: 1,
              min: xAxis.min,
              max: xAxis.max,
              interval: xAxis.interval,
              name: `${xAxis.name}(${xAxis.unit})`,
              nameLocation: 'middle',
              nameTextStyle: {
                padding: 16,
              },
              axisLine: {
                lineStyle: {
                  color: '#14141E'
                }
              },
              axisLabel: {
                showMinLabel: true
              },
            
            }
          ]
        }
        const yAxisesRes = []
        const legendData = []
        for (let i = 0; i < yLength; i++) {
          const y = yAxises[i]
          let gridIndex = 0
          let position = 'left'
          let offset = 0
          if (i === 0) {
            position = 'left'
          } else if (i === 1) {
            position = 'right'
          } else if (i === 2) {
            if (yLength % 3) {
              position = 'left'
              gridIndex = 1
            } else {
              position = 'right'
              offset = 80
            }
          } else {
            gridIndex = 1
            if (i === 3) {
              if (yLength % 3) {
                position = 'right'
              } else {
                position = 'left'
              }
            } else if (i === 4) {
              position = 'right'
              if (yLength % 3) {
                offset = 80
              } 
            } else if (i === 5) {
              position = 'right'
              offset = 80
            }
          }
          yAxisesRes.push({
            type: 'value',
            name: `${y.name}(${y.unit})`,
            gridIndex, 
            position, 
            offset, 
            min: y.min,
            max: y.max,
            interval: y.interval,
            axisLine: {
              show: true,
              lineStyle: {
                color: this.colors.deepCurveColors[y.name]
              }
            },
            axisLabel: {
              formatter: (value) => {
                return Math.round(value)
              } 
            },
            nameLocation: 'middle',
            nameTextStyle: {
              padding: 22
            },
          })
          legendData.push({ name: y.name })
        }
        option.yAxis = yAxisesRes
        option.legend = {
          data: legendData,
          top: 6,
          left: 54,
          height: 24,
        }
        const series = []
        for (let j = 0; j < curves.length; j++) {
          const curve = curves[j]
          const yAxisIndex = yAxisesRes.findIndex((y) => y.name === `${curve.yName}(${curve.yUnit})`)
          if (yAxisIndex >= 0) {
            const xAxisIndex = yAxisesRes[yAxisIndex] && yAxisesRes[yAxisIndex].gridIndex
            const color = this.colors.deepCurveColors[curve.yName] 
            const defaultOption = this.getDefaultSeriesOption(curve)
            const markPoint = this.getOperatingMarkPoint(curve.operatingPoint, curve.yName)
            series.push({
              ...defaultOption,
              xAxisIndex,
              yAxisIndex,
              color,
              markPoint
            })
          }
        }
        if (this.designPropertyData) {
          const parabolaSeries = this.getParabolaSeries()
          series.push(...parabolaSeries)
        }
        option.series = series
        this.showCurve()
      } else {
        console.log('y轴无信息')
      }
    },
    controlParabolas(value) {
      const parabolas = this.parabolas
      if (parabolas && parabolas.length) {
        const selectedNew = {}
        parabolas.forEach((p, index) => {
          const keyName = `parabola${index}`
          selectedNew[keyName] = value
        })
        this.option.legend.selected = {
          ...selectedNew
        }
      }
    },
    designChange(value) {
      this.showDesignPoints = value
      this.controlParabolas(value)
      this.showCurve(true)
    },
    // 控制运行点（调速曲线）展示 
    operatingChange(value) {
      this.showOperatingPoints = value
      if (this.speedRegulated) {
        this.controlSpeedRegulationCurves(value)
      } else {
        this.controlPerformanceMarkPoints(value)
      }
    },
    controlPerformanceMarkPoints(value) {
      if (this.operatingData) {
        const series = this.option.series
        const curves = this.performanceCurveData.curves
        for (let j = 0; j < curves.length; j++) {
          const curve = curves[j]
          const someSeries = series.find((s) => s.name === curve.yName)
          if (someSeries) {
            someSeries.markPoint = value ? this.getOperatingMarkPoint(curve.operatingPoint, curve.yName) : null
          }
        }
        this.showCurve(true)
      }
    },
    controlSpeedRegulationCurves(value) {
      value ? this.showSpeedRegulationCurves() : this.hideSpeedRegulationCurves()
    },
    // 展示调节曲线：series添加调速曲线，性能曲线运行点更新，扬程曲线和对应的调速曲线增加endLabel
    showSpeedRegulationCurves() {
      const { series, yAxis } = this.option
      const speedCurves = this.speedRegulationCurves
      if (speedCurves.length) {
        this.updateAxis()
        this.updateParabolas(series)
        for (let j = 0; j < speedCurves.length; j++) {
          const s = speedCurves[j]
          const yName = s.yName
          const markPoint = this.getOperatingMarkPoint(s.operatingPoint, yName)
          const syName = `speed${s.yName}`
          const ep = series.find((s) => s.name === yName)
          if (ep) {
            if (ep.name === '汽蚀余量') { 
              ep.markPoint = markPoint
              continue
            }
            if (ep.name === '扬程') {
              ep.endLabel = {
                show: true,
                align: 'left',
                formatter: () => {
                  return '100%\n'
                },
                color: '#50505A',
                distance: 5
              }
            }
            ep.markPoint = null
          }
          const es = series.find((s) => s.name === syName)
          if (es) {
            es.data = s.data
            es.markPoint = markPoint
          } else {
            const yAxisIndex = yAxis.findIndex((y) => y.name.indexOf(yName) >= 0)
            if (yAxisIndex >= 0) {
              const xAxisIndex = yAxis[yAxisIndex] && yAxis[yAxisIndex].gridIndex
              const color = this.colors.shallowCurveColors[yName] 
              const seriesOption = {
                name: syName,
                type: 'line',
                smooth: true,
                showSymbol: false,
                data: s.data
              }
              if (yName.includes('扬程')) {
                const that = this
                seriesOption.endLabel = {
                  show: true,
                  align: 'left',
                  formatter: function() {
                    const yMin = that.performanceCurveData.yAxises[0].rMin
                    const xMax = that.performanceCurveData.xAxises[0].rMax
                    const endPoint = s.data.slice(-1)[0]
                    // 不在当前曲线范围内的不展示
                    if (endPoint) {
                      if (endPoint[0] < xMax && endPoint[1] > yMin) {
                        return `\n${utils.timesValue(that.operatingData.rate, 100)}%`
                      }
                    }
                    return '\n'
                  },
                  color: '#50505A',
                  // offset: [0, 10]
                  distance: 5
                }
              }
              series.push({
                ...seriesOption,
                xAxisIndex,
                yAxisIndex,
                color,
                markPoint
              })
            }
          }
        }
        this.controlParabolas(this.showDesignPoints)
        this.showCurve(true)
      }
    },
    // 隐藏调节曲线：series剔除调速曲线，性能曲线运行点更新，扬程曲线endlabel取消
    hideSpeedRegulationCurves() {
      const curves = this.performanceCurveData.curves
      this.updateAxis()
      const oldSeries = this.option.series
      this.updateParabolas(oldSeries)
      const newSeries = oldSeries.reduce((arr, os) => {
        if (os.name.indexOf('speed') === -1) {
          if (os.name === '扬程') {
            os.endLabel.show = false
          }
          const c = curves.find((c) => c.yName === os.name)
          if (c) {
            os.data = c.data
            os.markPoint = this.getOperatingMarkPoint(c.operatingPoint, c.yName)
          }
          arr.push(os)
        }
        return arr
      }, [])
      this.option.series = [...newSeries]
      // 由于调速时图例的特殊处理，当再次点回不调速时，selected状态，应该不被记住，可利用需求点状态控制当前selected状态
      // 选择不调速：图例的抛物线应该受this.showDesignPoints控制
      this.controlParabolas(this.showDesignPoints)
      this.showCurve(true)
    },
    // 调速变化
    speedRegulateChange({ rate, speedRegulated }) {
      this.speedRegulated = speedRegulated
      console.log('utils.timesValue(rate, 0.01)', utils.timesValue(rate, 0.01))
      speedRegulated && this.$emit('refresh-curve-data', utils.timesValue(rate, 0.01))
      !speedRegulated && this.hideSpeedRegulationCurves()
    },
    // 坐标轴更新
    updateAxis() {
      const { xAxises, yAxises } = this.performanceCurveData
      const xA = xAxises[0]
      const { xAxis, yAxis } = this.option
      xAxis.forEach((x) => {
        x.min = this.speedRegulated ? xA.rMin : xA.min
        x.max = this.speedRegulated ? xA.rMax : xA.max
        x.interval = this.speedRegulated ? xA.rInterval : xA.interval
      })
      yAxis.forEach((y, i) => {
        const yA = yAxises[i]
        const yName = y.name
        const hasYName = SPEC_Y_AXIS.find((axis) => yName.includes(axis))
        y.min = this.speedRegulated && hasYName ? yA.rMin : yA.min
        y.max = this.speedRegulated && hasYName ? yA.rMax : yA.max
        y.interval = this.speedRegulated && hasYName ? yA.rInterval : yA.interval
      })
    },
    // 更新抛物线点
    updateParabolas(series) {
      const parabolas = this.parabolas
      if (series && series.length) {
        const that = this
        parabolas.forEach((p, i) => {
          const s = series.find((s) => s.name === `parabola${i}`)
          s.data = that.speedRegulated ? p.regulatedData : p.data
        })
      }
    }
  }
}
</script>
<style lang='less' scoped>
.pump-curve{
  margin: 0 auto;
}
.performance-curve-container {
  position: relative;
  padding-top: 10px;
  .select-section{
    padding-left: 12px;
  }
  .selector{
      padding-left: 24px;
      width: 120px;
    }
  .curve-selection-no-speed{
    position: absolute;
    left: 12px;top:16px;
    line-height: 24px;

  }
}
</style>
